const edsModule = (async () => {
  return await import('@it-enterprise/digital-signature')
})

export default {
  data: () => ({
    ds: null,
    keyType: {
      name: 'fileKey',
      value: 0
    },
    privateKeyIsSaved: false,
    isJKSContainer: false,
    JKSPrivateKeys: [],
    JKSPrivateKey: null,
    needPrivateKeyCertificates: false,
    privateKeyCertificates: null,
    signatureInfo: null,
    signResult: null,
    keyMedias: [],
    loading: false,
    error: null,
    progressBarValue: 0,
    qrCode: null,
    url: null
  }),



  watch: {
    fileKey (key) {
      if (!key || !(key instanceof File)) {
        this.isJKSContainer = false
        return
      }
      this.isJKSContainer = this.ds.isJKSContainer(key)
    },

    async isJKSContainer (value) {
      if (!value) {
        this.JKSPrivateKeys = []
        this.JKSPrivateKey = null
        return
      }
      this.JKSPrivateKeys = await this.ds.getJKSPrivateKeys(this.fileKey)
      this.JKSPrivateKey = this.JKSPrivateKeys[0]
    },

    async issuerCN (CA) {
      await this.ds.setCA(CA)
      this.needPrivateKeyCertificates = this.ds.needPrivateKeyCertificates()
    },

    keyType (type, oldType) {
      if (type === oldType) { return }
      this.initLocalState()
      this.setLibraryType()
    }
  },

  methods: {
    initLocalState () {
      this.password = null
      this.needPrivateKeyCertificates = false
      this.privateKeyCertificates = []
      this.signatureInfo = false
      this.isJKSContainer = false
      this.JKSPrivateKeys = []
      this.JKSPrivateKey = null
      this.error = null

      if (Array.isArray(this.issuerCNs) && this.issuerCNs.length > 0) {
        this.issuerCN = this.issuerCNs[0].value
      }
    },

    async setLibraryType () {
      this.loading = true
      this.ds || (await this.dsInit())
      try {
        await this.ds.setLibraryType()
        const { SW: hardKey } = (await edsModule()).Models.DigitalSignatureLibraryType
        if (this.keyType?.value === hardKey && !this.keyMedias.length) {
          this.keyMedias = await this.ds.getKeyMedias()
        }
      } catch (e) {
        console.error(e)
        this.error = e
      }
      this.loading = false
    },

    async dsInit () {
      const locale = this.$i18n.locale
      let { GraphQlUrl } = window.appConfig
      if (!GraphQlUrl.endsWith('/')) {
        GraphQlUrl += '/'
      }
      let userId = this.$authCore.auth.getUserData()?.id
      const models = new (await edsModule()).Models.DefaultSettingProvider(locale, userId, GraphQlUrl + 'api/ds')
      const DigitalSignature = (await edsModule()).DigitalSignature 
      this.ds = new  DigitalSignature(models)
    },

    async postInitActions () {
      const preferKeyType = await this.ds.initialise()
      this.keyType = this.signItems.find(type => type.value === preferKeyType)
      if (!this.keyType && this.signItems.length) {
        this.keyType = this.signItems[0]
      }
      const getCAsResult = await this.ds.getCAs()
      const issuerCNs = getCAsResult.map(ca => ({ text: ca.issuerCNs[0], value: ca }))
      const autoDetect = { text: this.$t('eds.autoDetect'), value: null }

      this.issuerCNs = [autoDetect, ...issuerCNs]
      this.issuerCN = this.issuerCNs[0].value
      this.ds.addConfirmKSPOperationEventListener(this.setQrCode)
    },
    setQrCode(data) {
      this.qrCode = data.qrCode
      this.url = data.url
      this.loading = false
    },
    async readPrivateKeyDiia(needGetCertificatesInformation) {
      await this.ds.readPrivateKeyDiia(needGetCertificatesInformation)
    },
    async toSign () {
      this.error = null
      this.loading = true
      try {
        if (!this.privateKeyIsSaved && this.keyType?.name !== 'diiaSignature') {
          await this.readPrivateKey()
        }

        if (!this.privateKeyIsSaved && this.keyType?.name === 'diiaSignature') {
          await this.readPrivateKeyDiia(false)
        }

        if (this.multipleSign) {
          let signs = await this.runMultipleSign()
          this.$options.popupController.resolve(signs)

        } else {
          let sign = await this.getSign(this.attachment)
          this.$options.popupController.resolve(sign)
        }

        this.showDialog = false
      } catch (e) {
        this.resetPrivateKey()
        this.error = e
      }
      this.loading = false
      this.resetKeyType()
    },
    resetKeyType() {
      this.keyType = this.signItems[0]
    },

    async readPrivateKey () {
      const { JS: fileKey, SW: hardKey } = (await edsModule()).Models.DigitalSignatureLibraryType
      if (this.keyType.value === fileKey) { await this.readFileKey() }
      if (this.keyType.value === hardKey) { await this.readHardwareKey() }
      // Save key in current session
      if (this.saveKeyOption === 'saveInSession') {
        window.ds = this.ds
        let privateKey = await this.ds.isPrivateKeyReaded()
        this.SET_PRIVATE_KEY(privateKey)
      }
    },
    SET_PRIVATE_KEY(value) {
      this.privateKeyIsSaved = value
    },
    readFileKey () {
      if (this.isJKSContainer && !this.JKSPrivateKey) { return }
      const privateKey = this.JKSPrivateKey
        ? this.JKSPrivateKey.privateKey
        : this.fileKey

      return this.ds.readFileKey(privateKey, this.password, this.privateKeyCertificates)
        .then(result => { this.signatureInfo = result.ownerInfo })
        .catch(e => {
          this.needPrivateKeyCertificates = e.code === 51
          throw Error(e.message)
        })
    },

    readHardwareKey () {
      this.hardwareKey.password = this.password
      return this.ds.readHardwareKey(this.hardwareKey, this.privateKeyCertificates)
        .then(result => { this.signatureInfo = result.ownerInfo })
        .catch(e => {
          this.needPrivateKeyCertificates = e.code === 51
          throw Error(e.message)
        })
    },

    async runMultipleSign () {
      this.progressBar = true

      let signs = []

      await Promise.all(
        this.attachments.map( async (attachment) => {
          signs.push({ sign: await this.getSign(attachment), file: attachment })
          this.progressBarValue++
        })
      )
      return signs
    },

    getSign (attachment) {
      const ds = this.ds || window.ds
      if (typeof (attachment) == 'string' || ArrayBuffer.isView(attachment)) {
        return ds.signDataEx(attachment, false)
          .catch(e => { throw Error(e.message) })
      }
      return ds.signFileEx(attachment.fileUrl, false)
        .catch(e => { throw Error(e.message) })
    },


    async reload () {
      this.loading = true
      this.ds || (await this.dsInit())
      try {
        await this.postInitActions()
        this.initLocalState()
        await this.setLibraryType()
        this.error = null
      } catch (e) {
        console.error(e)
        this.error = e
      }
      this.loading = false
    },

    resetPrivateKey () {
      if (window.ds) {
        window.ds.resetPrivateKey()
      }
      this.SET_PRIVATE_KEY(false)
    }
  }
}
